import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format, startOfWeek, addDays, isSameDay, parseISO, addWeeks, subWeeks } from "date-fns";
import { it } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Calendar, Clock, User, Plus, ChevronLeft, ChevronRight, LogOut, Menu, Eye, Edit, Trash2, Users, Phone, Mail, CalendarDays, List, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const StaffPortal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [staffInfo, setStaffInfo] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [calendars, setCalendars] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("day"); // day, week, staff (mobile default: day)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Booking dialog state
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    service_ids: [],
    staff_id: "",
    calendar_id: "",
    start_datetime: "",
    start_time: "",
    notes: "",
    status: "confirmed"
  });
  
  // Drag & drop state
  const [draggedBooking, setDraggedBooking] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(null);
  
  const token = localStorage.getItem("staff_token");
  
  useEffect(() => {
    if (!token) {
      navigate("/staff-login");
      return;
    }
    loadData();
  }, [token]);
  
  useEffect(() => {
    if (staffInfo) {
      loadBookings();
    }
  }, [currentDate, staffInfo]);
  
  const loadData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [meRes, staffRes, servicesRes, calendarsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/api/staff-portal/me`, { headers }),
        axios.get(`${API}/api/staff-portal/staff`, { headers }),
        axios.get(`${API}/api/staff-portal/services`, { headers }),
        axios.get(`${API}/api/staff-portal/calendars`, { headers }),
        axios.get(`${API}/api/staff-portal/categories`, { headers })
      ]);
      
      setStaffInfo(meRes.data);
      setStaff(staffRes.data);
      setServices(servicesRes.data);
      setCalendars(calendarsRes.data);
      setCategories(categoriesRes.data);
      
      if (meRes.data?.staff_id) {
        setBookingForm(prev => ({ ...prev, staff_id: meRes.data.staff_id }));
      }
    } catch (error) {
      console.error("Error loading data:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("staff_token");
        localStorage.removeItem("staff_info");
        navigate("/staff-login");
      }
      toast.error("Errore nel caricamento dei dati");
    }
    setLoading(false);
  };
  
  const loadBookings = async () => {
    setRefreshing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/api/staff-portal/bookings`, { headers });
      setBookings(response.data);
    } catch (error) {
      console.error("Error loading bookings:", error);
    }
    setRefreshing(false);
  };
  
  const handleLogout = () => {
    localStorage.removeItem("staff_token");
    localStorage.removeItem("staff_info");
    navigate("/staff-login");
  };
  
  // Parse datetime helper - MUST be defined before useMemo that uses it
  const parseLocalDateTime = (dateStr) => {
    if (!dateStr) return null;
    try {
      const cleanStr = dateStr.replace(/Z$/, '');
      const match = cleanStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):?(\d{2})?/);
      if (match) {
        const [, year, month, day, hours, minutes, seconds = '0'] = match;
        return new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
          parseInt(hours),
          parseInt(minutes),
          parseInt(seconds)
        );
      }
      return new Date(dateStr);
    } catch { return null; }
  };
  
  // Calendar navigation
  const weekDays = Array.from({ length: 7 }, (_, i) => 
    addDays(startOfWeek(currentDate, { weekStartsOn: 1 }), i)
  );
  
  // Dynamic hours based on bookings
  const hours = useMemo(() => {
    let minHour = 8;
    let maxHour = 20;
    
    bookings.forEach(booking => {
      if (booking.start_datetime) {
        const startDate = parseLocalDateTime(booking.start_datetime);
        const endDate = parseLocalDateTime(booking.end_datetime);
        if (startDate) {
          const startH = startDate.getHours();
          if (startH < minHour) minHour = startH;
        }
        if (endDate) {
          const endH = endDate.getHours() + 1;
          if (endH > maxHour) maxHour = endH;
        }
      }
    });
    
    return Array.from({ length: maxHour - minHour }, (_, i) => i + minHour);
  }, [bookings]);
  
  const goToPrevDay = () => setCurrentDate(addDays(currentDate, -1));
  const goToNextDay = () => setCurrentDate(addDays(currentDate, 1));
  const goToPrevWeek = () => setCurrentDate(subWeeks(currentDate, 1));
  const goToNextWeek = () => setCurrentDate(addWeeks(currentDate, 1));
  const goToToday = () => setCurrentDate(new Date());
  
  // Get bookings for a slot
  const getBookingsForSlot = (day, hour, staffId = null) => {
    return bookings.filter(booking => {
      if (!booking.start_datetime) return false;
      const bookingDate = parseLocalDateTime(booking.start_datetime);
      if (!bookingDate || !isSameDay(bookingDate, day)) return false;
      if (bookingDate.getHours() !== hour) return false;
      if (staffId && booking.staff_id !== staffId) return false;
      return true;
    });
  };
  
  // Get all bookings for a day (for list view)
  const getBookingsForDay = (day) => {
    return bookings
      .filter(booking => {
        if (!booking.start_datetime) return false;
        const bookingDate = parseLocalDateTime(booking.start_datetime);
        return bookingDate && isSameDay(bookingDate, day);
      })
      .sort((a, b) => {
        const dateA = parseLocalDateTime(a.start_datetime);
        const dateB = parseLocalDateTime(b.start_datetime);
        return (dateA?.getTime() || 0) - (dateB?.getTime() || 0);
      });
  };
  
  // Open booking dialog
  const openNewBooking = (day, hour, staffId = null) => {
    if (!staffInfo?.permissions?.can_edit_insert) {
      toast.error("Non hai i permessi per creare appuntamenti");
      return;
    }
    
    setEditMode(false);
    setSelectedBooking(null);
    setBookingForm({
      customer_name: "",
      customer_email: "",
      customer_phone: "",
      service_ids: [],
      staff_id: staffId || staffInfo?.staff_id || "",
      calendar_id: calendars[0]?.calendar_id || "",
      start_datetime: format(day, "yyyy-MM-dd"),
      start_time: hour !== undefined ? `${String(hour).padStart(2, '0')}:00` : "09:00",
      notes: "",
      status: "confirmed"
    });
    setBookingDialogOpen(true);
  };
  
  const openEditBooking = (booking) => {
    setEditMode(true);
    setSelectedBooking(booking);
    const startDate = parseLocalDateTime(booking.start_datetime);
    setBookingForm({
      customer_name: booking.customer_name || "",
      customer_email: booking.customer_email || "",
      customer_phone: booking.customer_phone || "",
      service_ids: booking.service_ids || [],
      staff_id: booking.staff_id || "",
      calendar_id: booking.calendar_id || "",
      start_datetime: startDate ? format(startDate, "yyyy-MM-dd") : "",
      start_time: startDate ? format(startDate, "HH:mm") : "",
      notes: booking.notes || "",
      status: booking.status || "confirmed"
    });
    setBookingDialogOpen(true);
  };
  
  // Save booking
  const handleSaveBooking = async (e) => {
    e.preventDefault();
    
    if (!staffInfo?.permissions?.can_edit_insert && !editMode) {
      toast.error("Non hai i permessi per creare appuntamenti");
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const startDateTime = `${bookingForm.start_datetime}T${bookingForm.start_time}:00`;
      
      const payload = {
        ...bookingForm,
        start_datetime: startDateTime
      };
      
      if (editMode && selectedBooking) {
        await axios.put(`${API}/api/staff-portal/bookings/${selectedBooking.booking_id}`, payload, { headers });
        toast.success("Prenotazione aggiornata!");
      } else {
        await axios.post(`${API}/api/staff-portal/bookings`, payload, { headers });
        toast.success("Prenotazione creata!");
      }
      
      setBookingDialogOpen(false);
      loadBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    }
  };
  
  // Delete booking
  const handleDeleteBooking = async () => {
    if (!staffInfo?.permissions?.can_delete) {
      toast.error("Non hai i permessi per eliminare appuntamenti");
      return;
    }
    
    if (!selectedBooking || !confirm("Sei sicuro di voler eliminare questa prenotazione?")) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/api/staff-portal/bookings/${selectedBooking.booking_id}`, { headers });
      toast.success("Prenotazione eliminata!");
      setBookingDialogOpen(false);
      loadBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };
  
  // Drag & Drop handlers
  const handleDragStart = (e, booking) => {
    if (!staffInfo?.permissions?.can_edit_insert) return;
    setDraggedBooking(booking);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragOver = (e, day, hour, staffId = null) => {
    e.preventDefault();
    const targetDate = new Date(day);
    targetDate.setHours(hour, 0, 0, 0);
    if (targetDate < new Date()) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    e.dataTransfer.dropEffect = 'move';
    setDragOverSlot({ day: day.toISOString(), hour, staffId });
  };
  
  const handleDrop = async (e, day, hour, staffId = null) => {
    e.preventDefault();
    setDragOverSlot(null);
    
    if (!draggedBooking || !staffInfo?.permissions?.can_edit_insert) return;
    
    const newDate = new Date(day);
    newDate.setHours(hour, 0, 0, 0);
    
    if (newDate < new Date()) {
      toast.error("Non puoi spostare un appuntamento nel passato");
      setDraggedBooking(null);
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/api/staff-portal/bookings/${draggedBooking.booking_id}/move`, {
        start_datetime: format(newDate, "yyyy-MM-dd'T'HH:mm:ss"),
        staff_id: staffId
      }, { headers });
      
      toast.success("Appuntamento spostato!");
      loadBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nello spostamento");
    }
    
    setDraggedBooking(null);
  };
  
  // Get booking duration
  const getBookingDuration = (booking) => {
    if (!booking.start_datetime || !booking.end_datetime) return 1;
    const start = parseLocalDateTime(booking.start_datetime);
    const end = parseLocalDateTime(booking.end_datetime);
    if (!start || !end) return 1;
    return Math.max((end - start) / (1000 * 60 * 60), 0.5);
  };
  
  // Get service name for booking
  const getServiceName = (booking) => {
    if (booking.services_details?.length > 0) {
      return booking.services_details.map(s => s.name).join(", ");
    }
    if (booking.service_ids?.length > 0) {
      return booking.service_ids.map(sid => {
        const service = services.find(s => s.service_id === sid);
        return service?.name || "Servizio";
      }).join(", ");
    }
    return "";
  };
  
  // Get staff name for booking
  const getStaffName = (booking) => {
    if (!booking.staff_id) return "";
    const staffMember = staff.find(s => s.staff_id === booking.staff_id);
    return staffMember?.name || "";
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="staff-portal-loading">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-500">Caricamento...</p>
        </div>
      </div>
    );
  }
  
  const canEdit = staffInfo?.permissions?.can_edit_insert;
  const canDelete = staffInfo?.permissions?.can_delete;
  const canViewAll = staffInfo?.permissions?.can_view_all_staff;
  const todaysBookings = getBookingsForDay(currentDate);
  
  return (
    <div className="min-h-screen bg-slate-50" data-testid="staff-portal">
      {/* Header - Responsive */}
      <header className="bg-white border-b sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-3">
          {/* Mobile Header */}
          <div className="flex items-center justify-between lg:hidden">
            <div className="flex items-center gap-2">
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="mobile-menu-button">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 p-0">
                  <SheetHeader className="p-4 border-b">
                    <SheetTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-primary" />
                      Portale Staff
                    </SheetTitle>
                  </SheetHeader>
                  <div className="p-4 space-y-4">
                    {/* Staff Info */}
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      {staffInfo?.photo_url ? (
                        <img src={staffInfo.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{staffInfo?.name}</p>
                        <p className="text-xs text-slate-500">{staffInfo?.business_name}</p>
                      </div>
                    </div>
                    
                    {/* Permissions */}
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-slate-500 uppercase">Permessi</p>
                      <div className="flex flex-wrap gap-1">
                        {canEdit && <Badge variant="outline" className="text-xs">Modifica</Badge>}
                        {canDelete && <Badge variant="outline" className="text-xs">Elimina</Badge>}
                        {canViewAll && <Badge variant="outline" className="text-xs">Vedi tutti</Badge>}
                      </div>
                    </div>
                    
                    {/* View Mode */}
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-slate-500 uppercase">Vista</p>
                      <div className="grid grid-cols-2 gap-2">
                        <Button 
                          variant={viewMode === "day" ? "default" : "outline"} 
                          size="sm"
                          className="w-full"
                          onClick={() => { setViewMode("day"); setMobileMenuOpen(false); }}
                        >
                          <CalendarDays className="h-4 w-4 mr-1" />
                          Giorno
                        </Button>
                        <Button 
                          variant={viewMode === "list" ? "default" : "outline"} 
                          size="sm"
                          className="w-full"
                          onClick={() => { setViewMode("list"); setMobileMenuOpen(false); }}
                        >
                          <List className="h-4 w-4 mr-1" />
                          Lista
                        </Button>
                        <Button 
                          variant={viewMode === "week" ? "default" : "outline"} 
                          size="sm"
                          className="w-full"
                          onClick={() => { setViewMode("week"); setMobileMenuOpen(false); }}
                        >
                          <Calendar className="h-4 w-4 mr-1" />
                          Settimana
                        </Button>
                        {canViewAll && (
                          <Button 
                            variant={viewMode === "staff" ? "default" : "outline"} 
                            size="sm"
                            className="w-full"
                            onClick={() => { setViewMode("staff"); setMobileMenuOpen(false); }}
                          >
                            <Users className="h-4 w-4 mr-1" />
                            Staff
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Logout */}
                    <Button 
                      variant="destructive" 
                      className="w-full mt-4" 
                      onClick={handleLogout}
                      data-testid="mobile-logout-button"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Esci
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
              <div>
                <h1 className="text-base sm:text-lg font-bold text-slate-900">Portale Staff</h1>
              </div>
            </div>
            
            <div className="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-9 w-9" 
                onClick={loadBookings}
                disabled={refreshing}
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              {canEdit && (
                <Button 
                  size="sm" 
                  className="h-9 px-3"
                  onClick={() => openNewBooking(currentDate)}
                  data-testid="mobile-add-booking"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          
          {/* Desktop Header */}
          <div className="hidden lg:flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-xl font-bold text-slate-900">Portale Staff</h1>
                <p className="text-sm text-slate-500">{staffInfo?.business_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {staffInfo?.photo_url ? (
                  <img src={staffInfo.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                )}
                <span className="font-medium">{staffInfo?.name}</span>
              </div>
              <div className="flex gap-1">
                {canEdit && <Badge variant="outline" className="text-xs">Modifica</Badge>}
                {canDelete && <Badge variant="outline" className="text-xs">Elimina</Badge>}
                {canViewAll && <Badge variant="outline" className="text-xs">Vedi tutti</Badge>}
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="desktop-logout-button">
                <LogOut className="h-4 w-4 mr-2" />
                Esci
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto p-2 sm:p-4">
        {/* Calendar Controls - Responsive */}
        <Card className="mb-3 sm:mb-4">
          <CardContent className="p-2 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              {/* Date Navigation */}
              <div className="flex items-center gap-1 sm:gap-2">
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="h-8 w-8 sm:h-9 sm:w-9"
                  onClick={viewMode === "week" || viewMode === "staff" ? goToPrevWeek : goToPrevDay}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-8 sm:h-9 px-2 sm:px-3 text-xs sm:text-sm"
                  onClick={goToToday}
                >
                  Oggi
                </Button>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="h-8 w-8 sm:h-9 sm:w-9"
                  onClick={viewMode === "week" || viewMode === "staff" ? goToNextWeek : goToNextDay}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <span className="ml-2 text-sm sm:text-base font-medium text-slate-700">
                  {viewMode === "week" || viewMode === "staff" 
                    ? `${format(weekDays[0], "d MMM", { locale: it })} - ${format(weekDays[6], "d MMM", { locale: it })}`
                    : format(currentDate, "EEEE d MMMM", { locale: it })
                  }
                </span>
              </div>
              
              {/* View Mode Toggle - Desktop only */}
              <div className="hidden lg:flex items-center gap-2">
                <Button 
                  variant={viewMode === "day" ? "default" : "outline"} 
                  size="sm"
                  onClick={() => setViewMode("day")}
                >
                  <CalendarDays className="h-4 w-4 mr-1" />
                  Giorno
                </Button>
                <Button 
                  variant={viewMode === "list" ? "default" : "outline"} 
                  size="sm"
                  onClick={() => setViewMode("list")}
                >
                  <List className="h-4 w-4 mr-1" />
                  Lista
                </Button>
                <Button 
                  variant={viewMode === "week" ? "default" : "outline"} 
                  size="sm"
                  onClick={() => setViewMode("week")}
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  Settimana
                </Button>
                {canViewAll && (
                  <Button 
                    variant={viewMode === "staff" ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setViewMode("staff")}
                  >
                    <Users className="h-4 w-4 mr-1" />
                    Per Staff
                  </Button>
                )}
              </div>
              
              {/* Add Booking - Desktop */}
              {canEdit && (
                <Button 
                  className="hidden lg:flex"
                  onClick={() => openNewBooking(currentDate)}
                  data-testid="desktop-add-booking"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nuovo Appuntamento
                </Button>
              )}
            </div>
            
            {/* Stats Row */}
            <div className="flex items-center gap-4 mt-3 pt-3 border-t text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {todaysBookings.length} appuntamenti oggi
              </span>
            </div>
          </CardContent>
        </Card>
        
        {/* Calendar Views */}
        <Card>
          <CardContent className="p-0">
            {/* LIST VIEW - Mobile Friendly */}
            {viewMode === "list" && (
              <div className="divide-y" data-testid="list-view">
                {todaysBookings.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Calendar className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p className="font-medium">Nessun appuntamento</p>
                    <p className="text-sm">per {format(currentDate, "EEEE d MMMM", { locale: it })}</p>
                  </div>
                ) : (
                  todaysBookings.map((booking) => {
                    const startDate = parseLocalDateTime(booking.start_datetime);
                    const endDate = parseLocalDateTime(booking.end_datetime);
                    const statusColors = {
                      confirmed: "bg-green-100 text-green-700 border-green-200",
                      pending: "bg-amber-100 text-amber-700 border-amber-200",
                      cancelled: "bg-red-100 text-red-700 border-red-200",
                      completed: "bg-blue-100 text-blue-700 border-blue-200"
                    };
                    
                    return (
                      <div 
                        key={booking.booking_id}
                        className="p-3 sm:p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => openEditBooking(booking)}
                        data-testid={`booking-item-${booking.booking_id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-slate-900 truncate">{booking.customer_name}</span>
                              <Badge className={`text-xs ${statusColors[booking.status] || statusColors.confirmed}`}>
                                {booking.status === 'confirmed' ? 'Confermato' :
                                 booking.status === 'pending' ? 'In attesa' :
                                 booking.status === 'cancelled' ? 'Annullato' : 'Completato'}
                              </Badge>
                            </div>
                            
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3.5 w-3.5" />
                                {startDate && format(startDate, "HH:mm")}
                                {endDate && ` - ${format(endDate, "HH:mm")}`}
                              </span>
                              
                              {getServiceName(booking) && (
                                <span className="truncate max-w-[150px]">{getServiceName(booking)}</span>
                              )}
                              
                              {getStaffName(booking) && canViewAll && (
                                <span className="flex items-center gap-1">
                                  <User className="h-3.5 w-3.5" />
                                  {getStaffName(booking)}
                                </span>
                              )}
                            </div>
                            
                            {(booking.customer_phone || booking.customer_email) && (
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-400 mt-1">
                                {booking.customer_phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="h-3 w-3" />
                                    {booking.customer_phone}
                                  </span>
                                )}
                                {booking.customer_email && (
                                  <span className="flex items-center gap-1 truncate max-w-[180px]">
                                    <Mail className="h-3 w-3" />
                                    {booking.customer_email}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <ChevronRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
            
            {/* DAY VIEW - Single Day Grid */}
            {viewMode === "day" && (
              <div className="overflow-auto" data-testid="day-view">
                <div className="min-w-[300px]">
                  {/* Time Grid */}
                  {hours.map((hour) => {
                    const slotBookings = getBookingsForSlot(currentDate, hour);
                    const isDropTarget = dragOverSlot?.hour === hour;
                    
                    return (
                      <div 
                        key={hour} 
                        className={`flex border-b ${isDropTarget ? "bg-blue-50" : ""}`}
                        style={{ minHeight: '70px' }}
                        onDragOver={(e) => handleDragOver(e, currentDate, hour)}
                        onDragLeave={() => setDragOverSlot(null)}
                        onDrop={(e) => handleDrop(e, currentDate, hour)}
                      >
                        <div className="w-14 sm:w-16 flex-shrink-0 p-2 text-xs sm:text-sm text-slate-500 text-right pr-2 bg-slate-50/50 border-r">
                          {hour}:00
                        </div>
                        <div className="flex-1 relative group p-1" style={{ minHeight: '70px' }}>
                          {canEdit && (
                            <button
                              onClick={() => openNewBooking(currentDate, hour)}
                              className="absolute top-1 right-1 z-10 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary/20"
                            >
                              <Plus className="h-4 w-4 text-primary" />
                            </button>
                          )}
                          
                          {slotBookings.map((booking, idx) => {
                            const duration = getBookingDuration(booking);
                            const heightPx = Math.max(duration * 70, 55);
                            const startDate = parseLocalDateTime(booking.start_datetime);
                            const startOffset = startDate ? (startDate.getMinutes() / 60) * 70 : 0;
                            
                            const statusColors = {
                              confirmed: "bg-green-100 border-green-300 text-green-800",
                              pending: "bg-amber-100 border-amber-300 text-amber-800",
                              cancelled: "bg-red-100 border-red-300 text-red-800",
                              completed: "bg-blue-100 border-blue-300 text-blue-800"
                            };
                            
                            return (
                              <div
                                key={booking.booking_id}
                                draggable={canEdit}
                                onDragStart={(e) => handleDragStart(e, booking)}
                                onDragEnd={() => setDraggedBooking(null)}
                                onClick={() => openEditBooking(booking)}
                                className={`absolute left-1 right-1 text-xs p-2 rounded-lg border cursor-pointer hover:shadow-md transition-all ${statusColors[booking.status] || statusColors.confirmed} ${canEdit ? 'cursor-grab active:cursor-grabbing' : ''}`}
                                style={{
                                  top: `${startOffset}px`,
                                  height: `${heightPx - 4}px`,
                                  zIndex: 10 + idx
                                }}
                                data-testid={`day-booking-${booking.booking_id}`}
                              >
                                <div className="font-semibold truncate">{booking.customer_name}</div>
                                <div className="text-[10px] opacity-70 flex items-center gap-1 mt-0.5">
                                  <Clock className="h-3 w-3" />
                                  {startDate && format(startDate, "HH:mm")}
                                </div>
                                {heightPx > 60 && getServiceName(booking) && (
                                  <div className="text-[10px] opacity-60 truncate mt-0.5">{getServiceName(booking)}</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* WEEK VIEW */}
            {viewMode === "week" && (
              <div className="overflow-x-auto" data-testid="week-view">
                <div className="min-w-[700px]">
                  {/* Header - Days */}
                  <div className="grid grid-cols-8 border-b bg-slate-50 sticky top-0">
                    <div className="p-2 text-center text-xs font-medium border-r"></div>
                    {weekDays.map((day) => (
                      <div 
                        key={day.toISOString()} 
                        className={`p-2 text-center border-r ${isSameDay(day, new Date()) ? 'bg-primary/10' : ''}`}
                      >
                        <div className="text-[10px] sm:text-xs text-slate-500 uppercase">{format(day, "EEE", { locale: it })}</div>
                        <div className={`text-base sm:text-lg font-medium ${isSameDay(day, new Date()) ? 'text-primary' : ''}`}>{format(day, "d")}</div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Time Grid */}
                  {hours.map((hour) => (
                    <div key={hour} className="grid grid-cols-8 border-b" style={{ minHeight: '60px' }}>
                      <div className="p-1 text-[10px] sm:text-xs text-slate-500 text-right pr-2 border-r bg-slate-50/50">
                        {hour}:00
                      </div>
                      {weekDays.map((day) => {
                        const slotBookings = getBookingsForSlot(day, hour);
                        const isDropTarget = dragOverSlot?.day === day.toISOString() && dragOverSlot?.hour === hour;
                        
                        return (
                          <div
                            key={`${day.toISOString()}-${hour}`}
                            className={`border-r relative group ${
                              isSameDay(day, new Date()) ? "bg-primary/5" : ""
                            } ${isDropTarget ? "bg-blue-100" : ""}`}
                            style={{ minHeight: '60px' }}
                            onDragOver={(e) => handleDragOver(e, day, hour)}
                            onDragLeave={() => setDragOverSlot(null)}
                            onDrop={(e) => handleDrop(e, day, hour)}
                          >
                            {canEdit && (
                              <button
                                onClick={() => openNewBooking(day, hour)}
                                className="absolute top-0.5 right-0.5 z-10 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <Plus className="h-3 w-3 text-primary" />
                              </button>
                            )}
                            
                            {slotBookings.map((booking, idx) => {
                              const duration = getBookingDuration(booking);
                              const heightPx = Math.max(duration * 60, 50);
                              const startDate = parseLocalDateTime(booking.start_datetime);
                              const startOffset = startDate ? (startDate.getMinutes() / 60) * 60 : 0;
                              
                              return (
                                <div
                                  key={booking.booking_id}
                                  draggable={canEdit}
                                  onDragStart={(e) => handleDragStart(e, booking)}
                                  onDragEnd={() => setDraggedBooking(null)}
                                  onClick={() => openEditBooking(booking)}
                                  className={`absolute left-0.5 right-0.5 text-[9px] sm:text-[10px] p-1 rounded border cursor-pointer hover:shadow transition-all ${
                                    booking.status === 'confirmed' ? 'bg-green-100 border-green-300 text-green-800' :
                                    booking.status === 'pending' ? 'bg-amber-100 border-amber-300 text-amber-800' :
                                    'bg-blue-100 border-blue-300 text-blue-800'
                                  } ${canEdit ? 'cursor-grab' : ''}`}
                                  style={{
                                    top: `${startOffset}px`,
                                    height: `${heightPx - 2}px`,
                                    zIndex: 10 + idx
                                  }}
                                >
                                  <div className="font-semibold truncate">{booking.customer_name}</div>
                                  <div className="opacity-70">{startDate && format(startDate, "HH:mm")}</div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* STAFF VIEW */}
            {viewMode === "staff" && canViewAll && staff.length > 0 && (
              <div className="overflow-x-auto" data-testid="staff-view">
                <div style={{ minWidth: `${Math.max(500, staff.length * 120 + 80)}px` }}>
                  {/* Header - Staff Names */}
                  <div className="grid border-b bg-slate-50 sticky top-0" style={{ gridTemplateColumns: `70px repeat(${staff.length}, 1fr)` }}>
                    <div className="p-2 text-center text-xs font-medium border-r">
                      {format(currentDate, "EEE d", { locale: it })}
                    </div>
                    {staff.map((s) => (
                      <div key={s.staff_id} className="p-2 text-center border-r">
                        <div className="flex flex-col items-center gap-1">
                          {s.photo_url || s.image_url ? (
                            <img src={s.photo_url || s.image_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="h-4 w-4 text-primary" />
                            </div>
                          )}
                          <span className="text-xs font-medium truncate max-w-full">{s.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Time Grid */}
                  {hours.map((hour) => (
                    <div key={hour} className="grid border-b" style={{ gridTemplateColumns: `70px repeat(${staff.length}, 1fr)`, minHeight: '60px' }}>
                      <div className="p-1 text-xs text-slate-500 text-right pr-2 border-r bg-slate-50/50">
                        {hour}:00
                      </div>
                      {staff.map((s) => {
                        const slotBookings = getBookingsForSlot(currentDate, hour, s.staff_id);
                        const isDropTarget = dragOverSlot?.hour === hour && dragOverSlot?.staffId === s.staff_id;
                        
                        return (
                          <div
                            key={`${s.staff_id}-${hour}`}
                            className={`border-r relative group ${isDropTarget ? "bg-blue-100" : ""}`}
                            style={{ minHeight: '60px' }}
                            onDragOver={(e) => handleDragOver(e, currentDate, hour, s.staff_id)}
                            onDragLeave={() => setDragOverSlot(null)}
                            onDrop={(e) => handleDrop(e, currentDate, hour, s.staff_id)}
                          >
                            {canEdit && (
                              <button
                                onClick={() => openNewBooking(currentDate, hour, s.staff_id)}
                                className="absolute top-0.5 right-0.5 z-10 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <Plus className="h-3 w-3 text-primary" />
                              </button>
                            )}
                            
                            {slotBookings.map((booking, idx) => {
                              const duration = getBookingDuration(booking);
                              const heightPx = Math.max(duration * 60, 50);
                              const startDate = parseLocalDateTime(booking.start_datetime);
                              const startOffset = startDate ? (startDate.getMinutes() / 60) * 60 : 0;
                              
                              return (
                                <div
                                  key={booking.booking_id}
                                  draggable={canEdit}
                                  onDragStart={(e) => handleDragStart(e, booking)}
                                  onDragEnd={() => setDraggedBooking(null)}
                                  onClick={() => openEditBooking(booking)}
                                  className={`absolute left-0.5 right-0.5 text-[10px] p-1 rounded border cursor-pointer hover:shadow transition-all ${
                                    booking.status === 'confirmed' ? 'bg-green-100 border-green-300 text-green-800' :
                                    booking.status === 'pending' ? 'bg-amber-100 border-amber-300 text-amber-800' :
                                    'bg-blue-100 border-blue-300 text-blue-800'
                                  } ${canEdit ? 'cursor-grab' : ''}`}
                                  style={{
                                    top: `${startOffset}px`,
                                    height: `${heightPx - 2}px`,
                                    zIndex: 10 + idx
                                  }}
                                >
                                  <div className="font-semibold truncate">{booking.customer_name}</div>
                                  <div className="opacity-70">{startDate && format(startDate, "HH:mm")}</div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* No Staff Message */}
            {viewMode === "staff" && (!canViewAll || staff.length === 0) && (
              <div className="text-center py-12 text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p className="font-medium">Vista staff non disponibile</p>
                <p className="text-sm">
                  {!canViewAll ? "Non hai i permessi per vedere tutti gli staff" : "Nessuno staff configurato"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      
      {/* Booking Dialog - Responsive */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="max-w-lg w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-xl">
              {editMode ? "Modifica Prenotazione" : "Nuova Prenotazione"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveBooking} className="space-y-4" data-testid="booking-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm">Nome Cliente *</Label>
                <Input 
                  value={bookingForm.customer_name}
                  onChange={(e) => setBookingForm({...bookingForm, customer_name: e.target.value})}
                  required
                  className="h-10"
                  data-testid="booking-customer-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Telefono</Label>
                <Input 
                  value={bookingForm.customer_phone}
                  onChange={(e) => setBookingForm({...bookingForm, customer_phone: e.target.value})}
                  className="h-10"
                  data-testid="booking-customer-phone"
                />
              </div>
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-sm">Email</Label>
              <Input 
                type="email"
                value={bookingForm.customer_email}
                onChange={(e) => setBookingForm({...bookingForm, customer_email: e.target.value})}
                className="h-10"
                data-testid="booking-customer-email"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm">Data *</Label>
                <Input 
                  type="date"
                  value={bookingForm.start_datetime}
                  onChange={(e) => setBookingForm({...bookingForm, start_datetime: e.target.value})}
                  required
                  className="h-10"
                  data-testid="booking-date"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Ora *</Label>
                <Input 
                  type="time"
                  value={bookingForm.start_time}
                  onChange={(e) => setBookingForm({...bookingForm, start_time: e.target.value})}
                  required
                  className="h-10"
                  data-testid="booking-time"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-sm">Servizio</Label>
                <Select 
                  value={bookingForm.service_ids[0] || ""} 
                  onValueChange={(v) => setBookingForm({...bookingForm, service_ids: v ? [v] : []})}
                >
                  <SelectTrigger className="h-10" data-testid="booking-service-select">
                    <SelectValue placeholder="Seleziona servizio" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((s) => (
                      <SelectItem key={s.service_id} value={s.service_id}>
                        {s.name} ({s.duration_minutes} min)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {canViewAll && (
                <div className="space-y-1.5">
                  <Label className="text-sm">Staff</Label>
                  <Select 
                    value={bookingForm.staff_id || ""} 
                    onValueChange={(v) => setBookingForm({...bookingForm, staff_id: v})}
                  >
                    <SelectTrigger className="h-10" data-testid="booking-staff-select">
                      <SelectValue placeholder="Seleziona staff" />
                    </SelectTrigger>
                    <SelectContent>
                      {staff.map((s) => (
                        <SelectItem key={s.staff_id} value={s.staff_id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {editMode && (
              <div className="space-y-1.5">
                <Label className="text-sm">Stato</Label>
                <Select 
                  value={bookingForm.status} 
                  onValueChange={(v) => setBookingForm({...bookingForm, status: v})}
                >
                  <SelectTrigger className="h-10" data-testid="booking-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confermato</SelectItem>
                    <SelectItem value="pending">In attesa</SelectItem>
                    <SelectItem value="cancelled">Annullato</SelectItem>
                    <SelectItem value="completed">Completato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-1.5">
              <Label className="text-sm">Note</Label>
              <Input 
                value={bookingForm.notes}
                onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})}
                placeholder="Note opzionali..."
                className="h-10"
                data-testid="booking-notes"
              />
            </div>
            
            <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-0 pt-2">
              {editMode && canDelete && (
                <Button 
                  type="button" 
                  variant="destructive" 
                  onClick={handleDeleteBooking}
                  className="w-full sm:w-auto sm:mr-auto"
                  data-testid="booking-delete-button"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Elimina
                </Button>
              )}
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setBookingDialogOpen(false)}
                  className="w-full sm:w-auto"
                >
                  Annulla
                </Button>
                <Button 
                  type="submit"
                  className="w-full sm:w-auto"
                  disabled={!canEdit && !editMode}
                  data-testid="booking-save-button"
                >
                  {editMode ? "Salva Modifiche" : "Crea Appuntamento"}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffPortal;
